<script>
  import { onMount, tick } from "svelte";
  import { LogoTriangle } from "nature-immersive-svelte-components";
  import CategoryList from "../CategoryList/index.svelte";
  import InformationList from "../InformationList/index.svelte";
  import StarIcon from "../StarIcon/index.svelte";
  import TrophyIcon from "../TrophyIcon/index.svelte";

  export let companyData;
  export let selectedCategoryOne;
  export let selectedCategoryTwo;

  let {
    categoryList,
    description,
    name,
    profileURL,
    status,
    winner,
  } = companyData;

  let expanded = true;
  let companyStack;

  async function toggleExpanded(event) {
    expanded = !expanded;
    // The tick function is a promise that resolves once any pending state changes have been applied
    // It's necessary to wait for the tick, otherwise we'll try to set focus on an element that
    // is hidden.
    await tick();
    // If the .company-stack box is expanded and if the click has come from a keyboard click, rather than
    // a mouse or a finger set focuse on the .company-stack
    // event.detail will be 0 for a a keyboard event and 1 or higher for other events
    // so we check for `!0` or 'not false'
    if (expanded && !event.detail) {
      companyStack.focus();
    }
  }

  onMount(() => {
    expanded = false;
  });
</script>

<style>
  h3 {
    max-width: none;
    margin: 0;
  }

  button {
    display: flex;
    align-items: center;
    width: calc(100% - 6px);
    max-width: none;
    padding: 0;
    margin-right: 3px;
    margin-left: 3px;
    font-size: var(--font-size-base);
    color: var(--black-1);
    cursor: pointer;
    background: none;
    border: none;
  }

  .company-stack {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    margin-top: var(--s0);
    margin-right: 3px;
    margin-left: 3px;
  }

  .company-stack:focus {
    outline: 3px solid var(--outline);
  }

  :global(.company-stack > *) {
    margin: 0;
  }

  :global(.company-stack > * + *) {
    margin-top: var(--s0);
  }

  :global(.company-selected-icon) {
    margin-left: auto;
  }

  :global(.toggle-icon) {
    margin-right: var(--s-3);
    transition: transform 0.3s ease;
    transform: rotate(0deg);
  }

  /* If prefers reduced motion is set, don't animate the
    triangle spinning */
  @media (prefers-reduced-motion: reduce) {
    :global(.toggle-icon) {
      transition: none;
    }
  }

  :global(button[aria-expanded="true"] .toggle-icon) {
    transform: rotate(180deg);
  }
</style>

<h3>
  <button aria-expanded="{expanded}" on:click="{toggleExpanded}">
    <LogoTriangle className="toggle-icon" height="0.5" />
    <span class="visually-hidden">Show panel</span>
    {name}
    {#if status && winner}
      <TrophyIcon
        className="company-selected-icon company-icon"
        title="Winner."
      />
    {:else if status}
      <StarIcon
        className="company-selected-icon company-icon"
        title="Selected by Nature."
      />
    {/if}
  </button>
</h3>

<div
  class="company-stack"
  hidden="{!expanded}"
  tabindex="-1"
  bind:this="{companyStack}"
>

  <div>
    <p class="visually-hidden">Categories:</p>
    <CategoryList {categoryList} {selectedCategoryOne} {selectedCategoryTwo} />
  </div>

  {#if profileURL}
    <div class="raised-card">
      <StarIcon
        className="company-icon company-icon__margin-right"
        height="0.8"
        title=""
      />
      <a class="faux-block-link" href="{profileURL}" target="_parent">
        Read
        <i>Nature</i>
        profile
      </a>
    </div>
  {/if}

  {#if description}
    <p>
      {@html description}
    </p>
  {/if}

  <div>
    <p class="visually-hidden">Information:</p>
    <InformationList {companyData} />
  </div>

</div>
